/**
 * Hariom ERP - Sales & CRM Module
 * Leads/CRM + Sales Orders. Both are entered by the sales team, editable,
 * deletable and persisted. Orders can be viewed/downloaded.
 */
import React, { useState } from 'react'
import { Plus, Edit3, Trash2, Download } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useLeadStore, useSalesOrderStore, type Lead, type SalesOrder } from '../../store/salesStore'
import FormModal, { type FormField } from '../../components/ui/FormModal'
import { exportRowsToExcel } from '../../utils/exportExcel'

const LEAD_STATUS: Record<string, string> = {
  new: 'badge-info', contacted: 'badge-info', qualified: 'badge-warning',
  proposal: 'bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full font-medium',
  negotiation: 'badge-warning', won: 'badge-success', lost: 'badge-danger',
}
const ORDER_STATUS: Record<string, string> = {
  draft: 'bg-steel-100 text-steel-600 text-xs px-2 py-0.5 rounded-full',
  confirmed: 'badge-info', processing: 'badge-warning',
  shipped: 'bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-medium',
  delivered: 'badge-success', cancelled: 'badge-danger',
}

const LEAD_FIELDS: FormField[] = [
  { name: 'company',  label: 'Company', required: true, placeholder: 'e.g. Apex Constructions' },
  { name: 'contact',  label: 'Contact Person', placeholder: 'e.g. Ramesh Shah' },
  { name: 'phone',    label: 'Phone', placeholder: 'e.g. 98765-43210' },
  { name: 'city',     label: 'City', placeholder: 'e.g. Mumbai' },
  { name: 'product',  label: 'Product', placeholder: 'e.g. Epoxy TMT' },
  { name: 'estTon',   label: 'Est. Tonnage', type: 'number', placeholder: '0' },
  { name: 'status',   label: 'Status', type: 'select', options: ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'] },
  { name: 'assigned', label: 'Assigned To', placeholder: 'e.g. Vikram S.' },
  { name: 'followUp', label: 'Follow-up Date', type: 'date' },
]
const ORDER_FIELDS: FormField[] = [
  { name: 'orderNo',  label: 'Order No', required: true, placeholder: 'e.g. SO-2026-045' },
  { name: 'customer', label: 'Customer', required: true, placeholder: 'e.g. SP SINGLA' },
  { name: 'items',    label: 'Items', placeholder: 'e.g. Epoxy TMT 12mm × 100T', full: true },
  { name: 'amount',   label: 'Amount (₹)', type: 'number', placeholder: '0' },
  { name: 'status',   label: 'Status', type: 'select', options: ['draft', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'] },
  { name: 'date',     label: 'Order Date', type: 'date' },
]

const num = (v: string) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0 }

type ModalState = { kind: 'lead' | 'order'; editing: Lead | SalesOrder | null }

const Sales: React.FC = () => {
  const [tab, setTab] = useState<'leads' | 'orders'>('leads')
  const { items: leads, add: addLead, update: updLead, remove: rmLead } = useLeadStore()
  const { items: orders, add: addOrder, update: updOrder, remove: rmOrder } = useSalesOrderStore()
  const [modal, setModal] = useState<ModalState | null>(null)

  const saveLead = (v: Record<string, string>) => {
    const rec: Lead = {
      id: (modal?.editing as Lead)?.id ?? `lead-${Date.now()}`,
      company: v.company.trim(), contact: v.contact.trim(), phone: v.phone.trim(),
      city: v.city.trim(), product: v.product.trim(), estTon: num(v.estTon),
      status: v.status || 'new', assigned: v.assigned.trim(), followUp: v.followUp,
    }
    if (modal?.editing) { updLead(rec.id, rec); toast.success(`Updated ${rec.company}`) }
    else { addLead(rec); toast.success(`Lead added`) }
    setModal(null)
  }
  const saveOrder = (v: Record<string, string>) => {
    const orderNo = v.orderNo.trim()
    if (orders.some(o => o.orderNo.toLowerCase() === orderNo.toLowerCase() && o.id !== (modal?.editing as SalesOrder)?.id)) {
      toast.error(`Order "${orderNo}" already exists`); return
    }
    const rec: SalesOrder = {
      id: (modal?.editing as SalesOrder)?.id ?? `so-${Date.now()}`,
      orderNo, customer: v.customer.trim(), items: v.items.trim(),
      amount: num(v.amount), status: v.status || 'draft', date: v.date,
    }
    if (modal?.editing) { updOrder(rec.id, rec); toast.success(`Updated ${rec.orderNo}`) }
    else { addOrder(rec); toast.success(`Order created`) }
    setModal(null)
  }

  const viewOrder = (o: SalesOrder) => {
    exportRowsToExcel(
      [{ 'Order No': o.orderNo, Customer: o.customer, Items: o.items, 'Amount (₹)': o.amount, Status: o.status, Date: o.date }],
      'Order', `Hariom_${o.orderNo}`
    )
    toast.success(`Downloaded ${o.orderNo}`)
  }

  const leadToRecord = (l: Lead): Record<string, string> => ({
    company: l.company, contact: l.contact, phone: l.phone, city: l.city, product: l.product,
    estTon: String(l.estTon), status: l.status, assigned: l.assigned, followUp: l.followUp,
  })
  const orderToRecord = (o: SalesOrder): Record<string, string> => ({
    orderNo: o.orderNo, customer: o.customer, items: o.items, amount: String(o.amount), status: o.status, date: o.date,
  })

  const pipeline = leads.filter(l => !['won', 'lost'].includes(l.status)).reduce((s, l) => s + l.estTon, 0)
  const revenue = orders.reduce((s, o) => s + o.amount, 0)

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Sales &amp; CRM</h1>
          <p className="page-subtitle">Manage leads, sales orders, and customer relationships</p>
        </div>
        <button
          onClick={() => setModal({ kind: tab === 'leads' ? 'lead' : 'order', editing: null })}
          className="btn-primary text-sm flex items-center gap-2"
        >
          <Plus size={14}/> {tab === 'leads' ? 'Add Lead' : 'New Order'}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card py-3 border-l-4 border-l-blue-500"><p className="text-xs text-steel-500">Total Leads</p><p className="text-2xl font-bold">{leads.length}</p></div>
        <div className="card py-3 border-l-4 border-l-green-500"><p className="text-xs text-steel-500">Deals Won</p><p className="text-2xl font-bold text-green-600">{leads.filter(l => l.status === 'won').length}</p></div>
        <div className="card py-3 border-l-4 border-l-primary-500"><p className="text-xs text-steel-500">Pipeline Value</p><p className="text-2xl font-bold">~{pipeline}T</p></div>
        <div className="card py-3 border-l-4 border-l-purple-500"><p className="text-xs text-steel-500">Revenue (Orders)</p><p className="text-2xl font-bold">₹{(revenue / 100000).toFixed(1)}L</p></div>
      </div>

      <div className="flex bg-steel-100 rounded-xl p-1 w-fit">
        <button onClick={() => setTab('leads')} className={clsx('px-4 py-2 rounded-lg text-sm font-medium transition-colors', tab === 'leads' ? 'bg-white text-steel-800 shadow' : 'text-steel-500')}>Leads / CRM</button>
        <button onClick={() => setTab('orders')} className={clsx('px-4 py-2 rounded-lg text-sm font-medium transition-colors', tab === 'orders' ? 'bg-white text-steel-800 shadow' : 'text-steel-500')}>Sales Orders</button>
      </div>

      {tab === 'leads' && (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-steel-50 border-b border-steel-200">
                <th className="table-header">Company</th><th className="table-header">Contact</th>
                <th className="table-header">City</th><th className="table-header">Product</th>
                <th className="table-header text-center">Est. (T)</th><th className="table-header">Status</th>
                <th className="table-header">Assigned</th><th className="table-header">Follow-up</th>
                <th className="table-header text-center">Action</th>
              </tr></thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-steel-400 text-sm py-10">No leads yet. Click <span className="font-semibold text-steel-600">“Add Lead”</span> to add one.</td></tr>
                ) : leads.map(l => (
                  <tr key={l.id} className="table-row-hover">
                    <td className="table-cell font-medium text-steel-800">{l.company}</td>
                    <td className="table-cell"><p className="text-sm">{l.contact}</p><p className="text-xs text-steel-400">{l.phone}</p></td>
                    <td className="table-cell text-sm">{l.city}</td>
                    <td className="table-cell text-sm">{l.product}</td>
                    <td className="table-cell text-center font-bold">{l.estTon}</td>
                    <td className="table-cell"><span className={LEAD_STATUS[l.status] || 'badge-info'}>{l.status}</span></td>
                    <td className="table-cell text-sm">{l.assigned || '—'}</td>
                    <td className="table-cell text-xs text-steel-500">{l.followUp || '—'}</td>
                    <td className="table-cell text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => setModal({ kind: 'lead', editing: l })} className="p-1.5 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit"><Edit3 size={14}/></button>
                        <button onClick={() => { if (window.confirm(`Delete lead ${l.company}?`)) { rmLead(l.id); toast.success('Deleted') } }} className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'orders' && (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-steel-50 border-b border-steel-200">
                <th className="table-header">Order No</th><th className="table-header">Customer</th>
                <th className="table-header">Items</th><th className="table-header text-right">Amount (₹)</th>
                <th className="table-header">Status</th><th className="table-header">Date</th>
                <th className="table-header text-center">Actions</th>
              </tr></thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr><td colSpan={7} className="text-center text-steel-400 text-sm py-10">No orders yet. Click <span className="font-semibold text-steel-600">“New Order”</span> to create one.</td></tr>
                ) : orders.map(o => (
                  <tr key={o.id} className="table-row-hover">
                    <td className="table-cell font-mono text-xs font-medium text-primary-700">{o.orderNo}</td>
                    <td className="table-cell font-medium">{o.customer}</td>
                    <td className="table-cell text-xs text-steel-500">{o.items}</td>
                    <td className="table-cell text-right font-bold">₹{o.amount.toLocaleString()}</td>
                    <td className="table-cell"><span className={ORDER_STATUS[o.status] || 'badge-info'}>{o.status}</span></td>
                    <td className="table-cell text-xs text-steel-500">{o.date || '—'}</td>
                    <td className="table-cell text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => viewOrder(o)} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center gap-1"><Download size={11}/> View</button>
                        <button onClick={() => setModal({ kind: 'order', editing: o })} className="p-1.5 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit"><Edit3 size={14}/></button>
                        <button onClick={() => { if (window.confirm(`Delete order ${o.orderNo}?`)) { rmOrder(o.id); toast.success('Deleted') } }} className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <FormModal
          title={modal.editing ? (modal.kind === 'lead' ? 'Edit Lead' : 'Edit Order') : (modal.kind === 'lead' ? 'Add Lead' : 'New Sales Order')}
          submitLabel={modal.editing ? 'Save Changes' : (modal.kind === 'lead' ? 'Add Lead' : 'Create Order')}
          fields={modal.kind === 'lead' ? LEAD_FIELDS : ORDER_FIELDS}
          initial={modal.editing ? (modal.kind === 'lead' ? leadToRecord(modal.editing as Lead) : orderToRecord(modal.editing as SalesOrder)) : {}}
          onClose={() => setModal(null)}
          onSave={modal.kind === 'lead' ? saveLead : saveOrder}
        />
      )}
    </div>
  )
}

export default Sales
